/**
 * User Management API Service - EVOP Inspector
 */

import { apiClient } from './api'
import { AxiosResponse } from 'axios'

// User and invitation types
export interface NotificationPreferences {
  system_emails: boolean
}

export interface User {
  id: number
  email: string
  name: string
  picture?: string
  role: 'EVO_ADMIN' | 'EVO_TEAM' | 'PSC_TEAM' | 'FP_USER'
  is_active: boolean
  created_at: string
  last_login?: string
  last_seen_at?: string
  invited_by?: number
  invitation_token?: string
  franchise_access?: UserFranchiseAccess[]
  allowed_markets?: string[]
  allowed_regions?: string[]
  allowed_features?: string[]
  notification_preferences?: NotificationPreferences
}

export interface UserFranchiseAccess {
  fp_id: number
  fp_code: string
  franchise_name: string
  granted_at: string
  granted_by: number
  invitation_token_used?: string
}

export interface InvitationLink {
  id: number
  token: string
  fp_id: number
  fp_code: string
  franchise_name: string
  created_by: number
  created_by_name: string
  created_at: string
  expires_at: string
  is_used: boolean
  usage_count: number
  last_used_at?: string
  last_used_by?: string
  invitation_url: string
}

export interface FPUserOtherAccess {
  fp_id: number
  fp_code: string
  franchise_name: string
}

export interface BulkRevokeFPAccessRequest {
  fp_ids: number[]
  delete_account_if_orphaned?: boolean
}

export interface BulkRevokeFPAccessResult {
  removed_fp_ids: number[]
  remaining_fp_count: number
  user_deleted: boolean
  user_deactivated: boolean
}

export interface FPUserAccess {
  id: number
  email: string
  name: string
  role: string
  is_active: boolean
  granted_at: string | null
  granted_by: string | null
  other_fp_accesses?: FPUserOtherAccess[]
}

export interface FPInvitation {
  id: number
  token: string
  created_by: string
  created_at: string | null
  expires_at: string | null
  used_count: number
  max_uses: number | null
  is_active: boolean
  is_usable: boolean
  invitation_url: string
  invited_email?: string
}

export interface CreateUserRequest {
  email: string
  name: string
  role: string
  allowed_markets?: string[]
  allowed_regions?: string[]
  allowed_features?: string[]
  fp_codes?: string[]
}

export interface UpdateUserRequest {
  role?: string
  is_active?: boolean
  allowed_markets?: string[]
  allowed_regions?: string[]
  allowed_features?: string[]
  notification_preferences?: NotificationPreferences
}

export interface CreateInvitationRequest {
  fp_code: string
  email: string
  fp_id?: number
  expires_in_days?: number
}

export interface InvitationValidation {
  valid: boolean
  token: string
  fp_code: string
  franchise_name: string
  expires_at: string
  message?: string
}

// User management API
export const userApi = {
  // List all users with optional filters
  async listUsers(role?: string, is_active?: boolean, search?: string): Promise<AxiosResponse<User[]>> {
    const params = new URLSearchParams()
    if (role) params.append('role', role)
    if (is_active !== undefined) params.append('is_active', String(is_active))
    if (search) params.append('search', search)

    return apiClient.get(`/admin/users${params.toString() ? '?' + params.toString() : ''}`)
  },

  // Get a specific user
  async getUser(userId: number): Promise<AxiosResponse<User>> {
    return apiClient.get(`/admin/users/${userId}`)
  },

  // Create a new user (Evolution/PSC team members)
  async createUser(data: CreateUserRequest): Promise<AxiosResponse<User>> {
    return apiClient.post('/admin/users', data)
  },

  // Update user details
  async updateUser(userId: number, data: UpdateUserRequest): Promise<AxiosResponse<User>> {
    return apiClient.put(`/admin/users/${userId}`, data)
  },

  // Deactivate a user
  async deactivateUser(userId: number): Promise<AxiosResponse<{ message: string }>> {
    return apiClient.delete(`/admin/users/${userId}`)
  },

  // Permanently delete a user (must be deactivated first)
  async deleteUser(userId: number, confirmEmail: string): Promise<AxiosResponse<{ message: string }>> {
    return apiClient.post(`/admin/users/${userId}/delete`, { confirm_email: confirmEmail })
  },

  // Grant FP access to a user
  async grantFPAccess(userId: number, fpCode: string): Promise<AxiosResponse<{ message: string }>> {
    return apiClient.post(`/admin/users/${userId}/franchise-access`, { fp_code: fpCode })
  },

  // Revoke FP access from a user
  async revokeFPAccess(userId: number, fpId: number): Promise<AxiosResponse<{ message: string }>> {
    return apiClient.delete(`/admin/users/${userId}/franchise-access/${fpId}`)
  },

  // Revoke a set of FP accesses in one transaction. When the removal leaves the
  // user with no FPs, the account is either deleted (delete_account_if_orphaned)
  // or auto-deactivated server-side.
  async revokeFPAccessBulk(
    userId: number,
    data: BulkRevokeFPAccessRequest,
  ): Promise<AxiosResponse<BulkRevokeFPAccessResult>> {
    return apiClient.post(`/admin/users/${userId}/revoke-fp-access`, data)
  },

  // Get users with access to a specific FP
  async getFPUsers(fpId: number): Promise<AxiosResponse<{ users: FPUserAccess[], invitations: FPInvitation[] }>> {
    return apiClient.get(`/admin/franchise-partners/${fpId}/users`)
  },
}

// Invitation management API
export const invitationApi = {
  // Create a new invitation
  async createInvitation(data: CreateInvitationRequest): Promise<AxiosResponse<InvitationLink>> {
    return apiClient.post('/admin/users/invitations', data)
  },

  // Revoke an invitation
  async revokeInvitation(invitationId: number): Promise<AxiosResponse<{ message: string }>> {
    return apiClient.put(`/admin/users/invitations/${invitationId}`, { is_active: false })
  },

  // Permanently delete a dead (expired/inactive/used-up) invitation
  async deleteInvitation(invitationId: number): Promise<AxiosResponse<{ message: string }>> {
    return apiClient.delete(`/admin/users/invitations/${invitationId}`)
  },

  // Validate an invitation token (public endpoint)
  async validateInvitation(token: string): Promise<AxiosResponse<InvitationValidation>> {
    return apiClient.get(`/invitations/${token}`)
  },

  // Accept an invitation (public endpoint)
  async acceptInvitation(token: string): Promise<AxiosResponse<{ message: string, redirect_url: string }>> {
    return apiClient.post(`/invitations/${token}/accept`)
  }
}